import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  ShoppingBag,
  Package,
  FileText,
  Users,
  Network,
  Leaf,
  Pill,
  ImageIcon,
  Grid3X3,
  Mail,
  Star,
  Video,
  Film,
  Settings,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '../../lib/cn'
import { useAuth } from '../../auth/AuthProvider'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** True = hidden from authors. False = visible to both admin and author. */
  adminOnly: boolean
}

interface NavGroup {
  label: string
  items: NavItem[]
}

/* Items grouped by domain so the eye lands on the right area without
 * scrolling through 15 sibling rows. adminOnly items are filtered at
 * render time — authors see a sidebar with only what they should
 * touch. The full list (with adminOnly flags) lives in a single
 * constant so the contract between roles is obvious in one place. */
const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, adminOnly: false },
    ],
  },
  {
    label: 'Sales',
    items: [
      { to: '/orders', label: 'Orders', icon: ShoppingBag, adminOnly: true },
      { to: '/products', label: 'Products', icon: Pill, adminOnly: true },
      { to: '/categories', label: 'Categories', icon: Grid3X3, adminOnly: false },
      { to: '/inventory', label: 'Inventory', icon: Package, adminOnly: true },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { to: '/hero', label: 'Hero', icon: ImageIcon, adminOnly: true },
      { to: '/blog', label: 'Blog', icon: FileText, adminOnly: false },
      { to: '/faq', label: 'FAQs', icon: HelpCircle, adminOnly: false },
      { to: '/reviews', label: 'Reviews', icon: Star, adminOnly: false },
      { to: '/testimonials', label: 'Testimonials', icon: Video, adminOnly: false },
      { to: '/video-testimonials', label: 'Video Reviews', icon: Film, adminOnly: false },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/users', label: 'Users', icon: Users, adminOnly: true },
      { to: '/affiliates', label: 'Affiliates', icon: Network, adminOnly: true },
      { to: '/contact', label: 'Contact', icon: Mail, adminOnly: true },
      { to: '/settings', label: 'Settings', icon: Settings, adminOnly: true },
    ],
  },
]

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'

  /* Filter items per role. Authors never see adminOnly links.
   * Admin sees everything. */
  const visibleGroups: NavGroup[] = isAdmin
    ? NAV_GROUPS
    : NAV_GROUPS.map((g) => ({
        label: g.label,
        items: g.items.filter((i) => !i.adminOnly),
      }))

  return (
    <aside className="w-60 shrink-0 bg-white border-r border-line flex flex-col h-full">
      <div className="h-14 px-5 flex items-center gap-2 border-b border-line">
        <div className="size-8 rounded-md bg-forest flex items-center justify-center">
          <Leaf className="size-4 text-white" />
        </div>
        <div className="leading-tight">
          <p className="font-display font-semibold text-sm text-ink">DrHerbal</p>
          <p className="text-[10px] text-slatey uppercase tracking-wider">Admin Console</p>
        </div>
      </div>

      <nav className="flex-1 py-3 overflow-y-auto">
        {visibleGroups.map((group, gi) => (
          <div key={group.label} className={cn(gi === 0 ? '' : 'mt-5')}>
            <p className="px-4 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slatey">
              {group.label}
            </p>
            <ul className="space-y-0.5 px-2">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/dashboard'}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        /* Uniform item geometry — px-3 py-1.5 across
                         * both active and inactive so the active row
                         * doesn't shift (the old border-l-2 trick did). */
                        'flex items-center gap-3 px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors',
                        isActive
                          ? 'bg-forest/10 text-forest'
                          : 'text-ink hover:bg-ivory',
                      )
                    }
                  >
                    <item.icon className="size-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="px-5 py-3 border-t border-line shrink-0">
        <p className="text-[10px] text-slatey">v1.0.0 · API: {import.meta.env.VITE_API_URL ?? 'local'}</p>
      </div>
    </aside>
  )
}
